import {
  EPreviewSize,
  SalesChannelModeWithSister,
} from '@/src/types/common/SalesChannel';
import {
  ANIMATION,
  GRID,
  MOUDLE_STYLE,
  PRODUCTS,
  TEXT,
  animationFields,
  moduleStyleFields,
  productTextFields,
} from './constant';
import {
  actionButtonTheme,
  editable,
  leaveGap,
  moduleTypeSelect,
  moduleWidthSelect,
  products,
  productToShow,
} from '@/src/tools/contentModulesFieldName';
import { ResponsiveContainer, ResponsiveField } from '../util';
import i18n from '../i18n';
import {
  EModuleWidth,
  EOverflowOption,
  EResponsiveProductToShow,
  EnumModuleType,
} from '@/src/types/common/module';
import { checkResponsiveProductGrid } from '@/src/constrains/SalesChannelContent-Revamp/ModuleList/validator';
import { gridTypeOptionsGenerator } from './tools';

export default (
  values: any,
  responsiveType: EPreviewSize,
  salesChannelMode: SalesChannelModeWithSister,
) => {
  const gridSetting = getGridSetting(values, responsiveType, salesChannelMode);
  const moduleStyleSetting = getModuleStyleTemplate(
    values,
    responsiveType,
    EnumModuleType.ResponsiveProductGrid,
  );
  const animationSetting = getAnimationTemplate(values, responsiveType);
  const productSetting = getProductsTemplate(values, responsiveType);
  return [
    {
      title: i18n['general.content.grid'],
      id: GRID,
      field: gridSetting,
      fields: [
        [moduleWidthSelect],
        [productToShow],
        [leaveGap],
        [actionButtonTheme],
      ],
    },
    {
      title: i18n['general.content.moduleStyle'],
      id: MOUDLE_STYLE,
      type: ResponsiveContainer.ModuleStyle,
      field: moduleStyleSetting,
      fields: moduleStyleFields,
    },
    {
      title: i18n['general.content.animation'],
      id: ANIMATION,
      field: animationSetting,
      fields: animationFields,
    },
    {
      title: i18n['general.content.text'],
      id: TEXT,
      field: [
        {
          field: ResponsiveField.ProductText,
          disabled:
            responsiveType !== EPreviewSize.WIDE ||
            !(
              responsiveType === EPreviewSize.WIDE ||
              values[`${TEXT}_${editable}`]
            ),
        },
      ],
      fields: productTextFields,
    },
    {
      title: i18n['general.content.products'],
      id: PRODUCTS,
      field: productSetting,
      fields: [[products]],
      headerErrorDependencies: [productToShow, products], //for section validation
      validator: (data, extra) => {
        const { checkProductsError } = checkResponsiveProductGrid();
        return checkProductsError(data, extra);
      },
    },
  ];
};

export const not_editable = [
  moduleTypeSelect,
  moduleWidthSelect,
  ...moduleStyle_not_editable,
  ...animation_not_editable,
  ...productTextFields,
];

export const getGridSetting = (
  values: any,
  responsiveType: EPreviewSize,
  salesChannelMode: SalesChannelModeWithSister,
) => {
  const v =
    responsiveType === EPreviewSize.WIDE || values[`${GRID}_${editable}`];
  let commonFields: Array<any> = [
    {
      field: ResponsiveField.GridType,
      disabled: responsiveType !== EPreviewSize.WIDE || !v,
      options: gridTypeOptionsGenerator({ salesChannelMode }),
    },
    {
      field: ResponsiveField.ModuleWidth,
      disabled: responsiveType !== EPreviewSize.WIDE || !v,
      options: [
        { label: i18n['general.content.none'], value: EModuleWidth.NONE },
        { label: i18n['general.content.wideWidth'], value: EModuleWidth.WIDE },
      ],
    },
    {
      field: ResponsiveField.ProductInRow,
      disabled: !v,
      options: productInRowOptions(responsiveType, true),
    },
  ];
  if (get(values, 'productToShow') !== EResponsiveProductToShow.One) {
    commonFields = [
      ...commonFields,
      {
        field: ResponsiveField.LeaveGap,
        disabled: !v,
      },
    ];
  }
  commonFields = [
    ...commonFields,
    {
      field: ResponsiveField.CallToActionButton,
      disabled: !v,
    },
  ];
  if (responsiveType !== EPreviewSize.WIDE) {
    commonFields = [
      ...commonFields,
      {
        field: ResponsiveField.OverflowItems,
        disabled: !v,
        options: [
          {
            label: i18n['general.overflow.carousel'],
            value: EOverflowOption.Carousel,
          },
          {
            label: i18n['general.overflow.anotherline'],
            value: EOverflowOption.AnotherLine,
          },
          { label: i18n['general.overflow.hide'], value: EOverflowOption.Hide },
        ],
      },
    ];
  }
  return commonFields;
};
import {
  animation_not_editable,
  getModuleStyleTemplate,
  moduleStyle_not_editable,
} from './imageCarousel';
import { getAnimationTemplate } from './imageCarousel';
import { replaceI18nVariables } from '@/src/tools/tools';
import { get } from 'lodash';

export const getProductsTemplate = (
  values: any,
  responsiveType: EPreviewSize,
) => {
  const v =
    responsiveType === EPreviewSize.WIDE || values[`${PRODUCTS}_${editable}`];
  return [
    {
      field: ResponsiveField.Products,
      disabled: !v,
    },
  ];
};

export const productInRowOptions = (
  breakpoint: EPreviewSize,
  isGrid?: boolean,
) => {
  switch (breakpoint) {
    case EPreviewSize.WIDE: {
      return [
        {
          label: replaceI18nVariables(
            i18n['general.productinrow.one'],
            i18n['general.label.product'],
          ),
          value: EResponsiveProductToShow.One,
        },
        {
          label: replaceI18nVariables(
            i18n['general.productinrow.twoportrait'],
            i18n['general.label.product'],
          ),
          value: EResponsiveProductToShow.TwoPortrait,
        },
        {
          label: replaceI18nVariables(
            i18n['general.productinrow.twolandscape'],
            i18n['general.label.product'],
          ),
          value: EResponsiveProductToShow.TwoLandscape,
        },
        {
          label: replaceI18nVariables(
            i18n['general.productinrow.three'],
            i18n['general.label.product'],
          ),
          value: EResponsiveProductToShow.Three,
        },
        {
          label: replaceI18nVariables(
            i18n['general.productinrow.four'],
            i18n['general.label.product'],
          ),
          value: EResponsiveProductToShow.Four,
        },
        {
          label: replaceI18nVariables(
            i18n['general.productinrow.five'],
            i18n['general.label.product'],
          ),
          value: EResponsiveProductToShow.Five,
        },
        {
          label: replaceI18nVariables(
            i18n['general.productinrow.six'],
            i18n['general.label.product'],
          ),
          value: EResponsiveProductToShow.Six,
        },
      ];
    }
    case EPreviewSize.TABLET: {
      return [
        {
          label: replaceI18nVariables(
            i18n['general.productinrow.one'],
            i18n['general.label.product'],
          ),
          value: EResponsiveProductToShow.One,
        },
        {
          label: replaceI18nVariables(
            i18n['general.productinrow.twoportrait'],
            i18n['general.label.product'],
          ),
          value: EResponsiveProductToShow.TwoPortrait,
        },
        {
          label: replaceI18nVariables(
            i18n['general.productinrow.twolandscape'],
            i18n['general.label.product'],
          ),
          value: EResponsiveProductToShow.TwoLandscape,
        },
        {
          label: replaceI18nVariables(
            i18n['general.productinrow.three'],
            i18n['general.label.product'],
          ),
          value: EResponsiveProductToShow.Three,
        },
      ];
    }
    case EPreviewSize.MOBILE: {
      return [
        {
          label: replaceI18nVariables(
            i18n['general.productinrow.one'],
            i18n['general.label.product'],
          ),
          value: EResponsiveProductToShow.One,
        },
        isGrid
          ? {
              label: replaceI18nVariables(
                i18n['general.productinrow.two'],
                i18n['general.label.product'],
              ),
              value: EResponsiveProductToShow.Two,
            }
          : {
              label: replaceI18nVariables(
                i18n['general.productinrow.twoportrait'],
                i18n['general.label.product'],
              ),
              value: EResponsiveProductToShow.TwoPortrait,
            },
        {
          label: replaceI18nVariables(
            i18n['general.productinrow.three'],
            i18n['general.label.product'],
          ),
          value: EResponsiveProductToShow.Three,
        },
      ];
    }
  }
};
